import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Link } from '@react-pdf/renderer';
import { WorksheetState } from '@src/stores/worksheet.store';
import { formatQualification } from '@src/utils/utils';

type Question = {
  id: string;
  title: string;
  qualification: string;
  imageSrc: string;
  markSchemeSrc?: string;
  writtenMsSrc?: string;
  markSchemeUrl?: string;
  writtenMsUrl?: string;
  categories?: unknown[];
  areas?: any[];
  subtopics?: any[];
};

type Worksheet = {
  questions: Question[];
  cover?: WorksheetState['cover'];
  incrementQuestions?: boolean;
  hasQrCode?: boolean;
  hasSubtopics?: boolean;
  type: 'worksheet' | 'markScheme';
};

const hasLength = (arr: any[] | undefined) => arr && arr.length > 0;

const QB_ROOT_PATH = 'https://mymathscloud.ams3.digitaloceanspaces.com/QuestionBank/';

const styles = StyleSheet.create({
  // Use a column layout for the cover page
  coverPage: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    position: 'relative',
  },
  header: {
    position: 'absolute',
    top: 10,
    right: 10,
    fontSize: 9,
    fontStyle: 'italic',
  },
  coverSection: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  coverTitle: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 10,
    color: '#fd99c5',
  },
  coverSubtitle: {
    fontSize: 14,
    marginTop: 5,
    color: '#23d4ff',
  },
  coverText: {
    fontSize: 10,
    marginTop: 10,
    color: 'black',
    fontFamily: 'Helvetica-Bold',
  },
  page: {
    flexDirection: 'row',
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
  title: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 10,
    color: '#23d4ff',
  },
  subText: {
    fontSize: 7,
    marginBottom: 5,
  },
  imageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginBottom: 10,
  },
  imageQ: {
    width: '1000',
    objectFit: 'contain',
  },
  imageM: {
    width: 1000,
    height: '80%',
    objectFit: 'contain',
  },
  qrContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0,
    padding: '0 10',
    width: '100%',
  },
  qrDiv: {
    fontSize: 8,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 5,
  },
  qrImage: {
    width: 75,
    height: 75,
    border: '2px solid black',
    padding: 2,
    borderRadius: 6,
  },
  qrText: {
    fontSize: 8,
    textTransform: 'uppercase',
    backgroundColor: 'black',
    color: 'white',
    padding: '2 5',
    borderRadius: '25%',
  },
  qrTitle: {
    fontFamily: 'Helvetica-Bold',
    color: '#fd99c5',
    fontSize: 10,
  },
  subTextSpan: {
    color: '#6b6b6b',
  },
  solutionPage: {
    flexDirection: 'column',
    padding: 30,
    position: 'relative',
  },
  lineContainer: {
    flexDirection: 'column',
    marginTop: 30,
    flex: 1,
  },
  line: {
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    borderBottomStyle: 'solid',
    height: 25,
    marginBottom: 0,
  },
});

const CornerBanner = () => (
  <Link
    style={{
      position: 'absolute',
      top: 0,
      right: 0,
    }}
    src="https://mymathscloud.com"
  >
    <Image
      style={{ width: 100, height: 100 }}
      src="https://www.datocms-assets.com/34817/1738836657-corner-graphic.png"
    />
  </Link>
);

const WorksheetPDF = ({
  questions,
  cover,
  incrementQuestions,
  hasQrCode,
  hasSubtopics,
}: Worksheet) => (
  <Document>
    {/* Cover Page */}
    {cover && (
      <Page size="A4" style={styles.coverPage}>
        <CornerBanner />
        <View style={styles.coverSection}>
          {cover.title && <Text style={styles.coverTitle}>{cover.title}</Text>}
          {cover.subtitle && <Text style={styles.coverSubtitle}>{cover.subtitle}</Text>}
          {cover.comments && <Text style={styles.coverText}>{cover.comments}</Text>}
          {cover.timeAllowed && <Text style={styles.coverText}>Time: {cover.timeAllowed}</Text>}
          {cover.totalMarks && <Text style={styles.coverText}>Marks: {cover.totalMarks}</Text>}
          {cover.startDate && <Text style={styles.coverText}>{cover.startDate}</Text>}

          {hasSubtopics && (
            <View
              style={{
                maxWidth: 400,
                margin: '0 auto',
                alignItems: 'center',
                ...styles.coverText,
              }}
            >
              <Text style={{ textAlign: 'center' }}>
                Questions in past papers often come up combined with other topics.
              </Text>
              <Text style={{ textAlign: 'center' }}>
                Topic tags have been given for each question to enable you to know if you can do the
                question or whether you need to wait to cover the additional topic(s).
              </Text>
            </View>
          )}

          {hasQrCode && (
            <Text style={styles.coverText}>
              Scan the QR code(s) or click the link for instant detailed model solutions!
            </Text>
          )}
        </View>
      </Page>
    )}

    {/* Question Pages */}
    {questions.map((question) => {
      const codes = [
        { title: 'Mark Scheme', src: question.markSchemeSrc, url: question.markSchemeUrl },
        { title: 'Written Mark Scheme', src: question.writtenMsSrc, url: question.writtenMsUrl },
      ];
      // console.log(codes);
      const includeCodes = codes.some((code) => code.src);

      return (
        <React.Fragment key={question.id}>
          {/* Question Page */}
          <Page size="A4" style={styles.page}>
            <CornerBanner />
            <View style={styles.section}>
              <Text style={styles.title}>{incrementQuestions ? question.title : null}</Text>

              {question.qualification && (
                <Text style={styles.subText}>
                  Qualification:{' '}
                  <Text style={styles.subTextSpan}>
                    {formatQualification(question.qualification)}
                  </Text>
                </Text>
              )}

              {/* Categories */}
              {hasLength(question.areas) && (
                <Text style={styles.subText}>
                  Areas: <Text style={styles.subTextSpan}>{question.areas?.join(', ')}</Text>
                </Text>
              )}

              {hasLength(question.subtopics) && (
                <Text style={styles.subText}>
                  Subtopics:{' '}
                  <Text style={styles.subTextSpan}>{question.subtopics?.join(', ')}</Text>
                </Text>
              )}

              {hasLength(question.categories) && (
                <Text style={styles.subText}>{question.categories?.join(' / ')}</Text>
              )}

              {/* Image */}
              <Image
                src={question.imageSrc}
                style={{
                  maxHeight: 675,
                  margin: '0 auto',
                  width: 450,
                  // objectFit: 'contain',
                }}
              />

              {/* QR Codes */}
              {includeCodes && (
                <View style={styles.qrContainer}>
                  {codes.map((code, idx) => {
                    const emptyUrlField = code.url === QB_ROOT_PATH;
                    if (!code.url || emptyUrlField) return null;
                    return (
                      <View key={idx} style={styles.qrDiv}>
                        <Text style={styles.qrText}>Scan Me!</Text>
                        <Image src={code.src} style={styles.qrImage} />
                        <Text style={styles.qrTitle}>{code.title}</Text>
                        <Link src={code.url}>View Online</Link>
                      </View>
                    );
                  })}
                </View>
              )}
            </View>
          </Page>

          {/* Lined Solution Page */}
          <Page size="A4" style={styles.solutionPage}>
            <CornerBanner />

            <View style={styles.lineContainer}>
              {Array.from({ length: 28 }, (_, lineIndex) => (
                <View key={lineIndex} style={styles.line} />
              ))}
            </View>
          </Page>
        </React.Fragment>
      );
    })}
  </Document>
);

export default WorksheetPDF;
